// The Talking Head's cast (issue 238, live's `talking-head-form/schema.ts`, `utils.ts` and its
// hooks): the actors in the form, the voice each speaks with, its voiceover, and live's rules
// on when the voiceovers can be made and when the videos can start.
import type { Job, Voice } from "../../../../api/generated/model";
import type { PickedActor } from "../../../../platform/actors/picked";

/** Live's caps (`schema.ts`): four actors, and a script of ten characters for the videos. */
export const MAX_ACTORS = 4;
export const MIN_SCRIPT = 10;

/** Live's tooltip lines on a grey Generate Audios (`generate-audios-button/constants.ts`). */
export const NEEDS_VOICES = "Add voices to all actors to generate audio";
export const NEEDS_SCRIPT = "Add a script to generate audio";

/** An actor in the form. */
export interface Cast {
  /** A global or team actor, or a library image (Add Actors' pick). */
  actor: PickedActor;
  /** A voice picked in Voice Settings; none leaves the actor's own, live's recommended one. */
  picked: Voice | null;
  /** Live's Stability for this actor, sent as picked (live drops it, bug 71); none until set. */
  stability: number | null;
  /** The actor's voiceover job since the last Generate Audios; a new voice or script clears it. */
  take: string | null;
}

export function castOf(actor: PickedActor): Cast {
  return { actor, picked: null, stability: null, take: null };
}

/** The voice the actor speaks with: the one picked, else its own; none for an actor without. */
export function voiceOf(cast: Cast): { id: string; name: string } | null {
  if (cast.picked !== null) return { id: cast.picked.id, name: cast.picked.name };
  return cast.actor.voice;
}

/**
 * Live's Confirm in Add Actors: the actors picked, in the dialog's order. One already in the
 * form keeps its voice, its stability and its voiceover; a new one comes with its own voice.
 */
export function recast(current: readonly Cast[], actors: readonly PickedActor[]): Cast[] {
  return actors.map((actor) => current.find((each) => each.actor.key === actor.key) ?? castOf(actor));
}

/**
 * Why Generate Audios is grey, in live's lines and order; none when it can run. An actor "Add AI
 * Actor" is still making has no voice yet, so it asks for voices too, as live's pending card.
 */
export function audioBlockers(casts: readonly Cast[], script: string, making = false): string[] {
  const lines: string[] = [];
  if (casts.length === 0 || making || casts.some((cast) => voiceOf(cast) === null)) {
    lines.push(NEEDS_VOICES);
  }
  if (script.trim() === "") lines.push(NEEDS_SCRIPT);
  return lines;
}

/** Where an actor's voiceover is, read from its job. */
export type TakeState =
  | { state: "none" }
  | { state: "running" }
  | { state: "done"; url: string; durationMs: number | null }
  | { state: "failed" };

export function takeOf(job: Job | undefined, jobId: string | null): TakeState {
  if (jobId === null) return { state: "none" };
  if (job === undefined) return { state: "running" };
  if (job.status === "succeeded") {
    const output = job.outputs[0];
    return output?.url === null || output?.url === undefined
      ? { state: "failed" }
      : { state: "done", url: output.url, durationMs: output.duration_ms };
  }
  if (job.status === "failed" || job.status === "expired" || job.status === "canceled") {
    return { state: "failed" };
  }
  return { state: "running" };
}

/** Live's four labels on the voiceovers' button (`generate-audios-button/constants.ts`). */
export type AudioButton = "Generate Audios" | "Generating Audio" | "Audio generated" | "Re-generate Audios";

/**
 * The button's word: "Generating Audio" while a voiceover runs or starts, "Audio generated" for
 * live's 3 s once they are all made, then "Re-generate Audios", as after a failure; before any,
 * "Generate Audios".
 */
export function audioButton(input: {
  generated: boolean;
  running: boolean;
  justMade: boolean;
}): AudioButton {
  if (input.running) return "Generating Audio";
  if (input.justMade) return "Audio generated";
  return input.generated ? "Re-generate Audios" : "Generate Audios";
}

/** An audio's length against the model's limits, in live's upload words (`media-validation.ts`). */
export function lengthProblem(
  durationMs: number | null,
  limits: { min_seconds: number | null; max_seconds: number | null },
): string | null {
  if (durationMs === null) return null;
  const seconds = durationMs / 1000;
  if (limits.min_seconds !== null && seconds < limits.min_seconds) {
    return `Audio duration is too short (${seconds.toFixed(1)}s). Min: ${limits.min_seconds}s.`;
  }
  if (limits.max_seconds !== null && seconds > limits.max_seconds) {
    return `Audio duration is too long (${seconds.toFixed(1)}s). Max: ${limits.max_seconds}s.`;
  }
  return null;
}
